import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { PipelineRisk, ChartTooltipProps } from '@/types';

interface FALLOUTRiskChartProps {
  data: PipelineRisk[];
}

const FALLOUTRiskChart: React.FC<FALLOUTRiskChartProps> = ({ data }) => {
  const CustomTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy-card border border-navy-border rounded-lg p-3 shadow-xl">
          <p className="text-xs text-text-heading font-medium">{label}</p>
          <p className="text-sm text-gold-primary font-bold">{payload[0].value}%</p>
          <p className="text-[10px] text-text-muted">Risk Level</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-6">
      <h3 className="text-base font-semibold text-text-heading mb-4">FALLOUT Pipeline Risk</h3>
      
      <div className="h-56 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="horizontal" margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <XAxis type="number" domain={[0, 30]} axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 11 }} />
            <YAxis type="category" dataKey="category" axisLine={false} tickLine={false} tick={{ fill: '#CBD5E1', fontSize: 11 }} width={130} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {data.map((risk, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: risk.color }} />
              <span className="text-xs text-text-primary">{risk.category}</span>
            </div>
            <span className="text-xs font-semibold text-gold-primary">{risk.value}%</span>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-5 pt-4 border-t border-navy-border/50 grid grid-cols-2 gap-4 text-center">
        <div>
          <p className="text-lg font-bold text-red-400">{Math.max(...data.map(d => d.value))}%</p>
          <p className="text-[10px] text-text-muted">Highest Risk</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gold-primary">
            {(data.reduce((s, d) => s + d.value, 0) / data.length).toFixed(1)}%
          </p>
          <p className="text-[10px] text-text-muted">Average Risk</p>
        </div>
      </div>
    </div>
  );
};

export default FALLOUTRiskChart;
